import { useRef, useState, useEffect } from 'react';
import { useTagsSearch } from '../../hooks/useTagsSearch';

const labelClass =
  'block text-xs uppercase tracking-widest text-primary-600 dark:text-primary-400 mb-2';
const inputClass =
  'w-full px-4 py-3 border border-primary-200 dark:border-primary-800 bg-white dark:bg-primary-950 text-primary-950 dark:text-white focus:outline-none focus:border-primary-400 transition-colors duration-300';

export interface TagComboboxProps {
  id?: string;
  label?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: string;
  'aria-label'?: string;
}

export function TagCombobox({
  id: idProp,
  label = 'Etiqueta',
  value,
  onChange,
  placeholder = 'ex: Cozinha, Sala de estar',
  error,
  'aria-label': ariaLabel,
}: TagComboboxProps) {
  const id = idProp ?? `tag-combobox-${Math.random().toString(36).slice(2, 9)}`;
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);

  const { tags, loading } = useTagsSearch(value);

  const showList = isOpen && (value.trim().length >= 1);
  const suggestions = tags;

  useEffect(() => {
    setHighlightIndex(-1);
  }, [value, suggestions.length]);

  useEffect(() => {
    if (!showList) return;
    const handle = (e: MouseEvent) => {
      if (containerRef.current?.contains(e.target as Node)) return;
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [showList]);

  const handleSelect = (name: string) => {
    onChange(name);
    setIsOpen(false);
    setHighlightIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showList || suggestions.length === 0) {
      if (e.key === 'Escape') setIsOpen(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => (i < suggestions.length - 1 ? i + 1 : i));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => (i > 0 ? i - 1 : -1));
      return;
    }
    if (e.key === 'Enter' && highlightIndex >= 0 && suggestions[highlightIndex]) {
      e.preventDefault();
      handleSelect(suggestions[highlightIndex].name);
      return;
    }
    if (e.key === 'Escape') {
      setIsOpen(false);
      setHighlightIndex(-1);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      {label && (
        <label htmlFor={id} className={labelClass}>
          {label}
        </label>
      )}
      <input
        id={id}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setIsOpen(true)}
        onKeyDown={handleKeyDown}
        className={inputClass}
        placeholder={placeholder}
        autoComplete="off"
        aria-label={ariaLabel ?? label}
        aria-autocomplete="list"
        aria-expanded={showList}
        aria-controls={showList ? `${id}-listbox` : undefined}
        role="combobox"
      />
      {showList && (
        <ul
          id={`${id}-listbox`}
          role="listbox"
          className="absolute z-10 mt-1 w-full border border-primary-200 dark:border-primary-800 bg-white dark:bg-primary-950 shadow max-h-48 overflow-auto"
        >
          {loading ? (
            <li className="px-4 py-3 text-primary-600 dark:text-primary-400 text-sm">
              Carregando...
            </li>
          ) : suggestions.length === 0 ? (
            <li className="px-4 py-3 text-primary-600 dark:text-primary-400 text-sm">
              Nenhuma etiqueta encontrada. Digite e salve para criar.
            </li>
          ) : (
            suggestions.map((tag, i) => (
              <li
                key={tag.id}
                role="option"
                aria-selected={highlightIndex === i}
                className={`px-4 py-3 text-primary-950 dark:text-white cursor-pointer font-light transition-colors duration-300 ${
                  highlightIndex === i
                    ? 'bg-primary-100 dark:bg-primary-800'
                    : 'hover:bg-primary-50 dark:hover:bg-primary-900'
                }`}
                onMouseEnter={() => setHighlightIndex(i)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(tag.name);
                }}
              >
                {tag.name}
              </li>
            ))
          )}
        </ul>
      )}
      {error && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
}
